import { createAsyncThunk, AsyncThunk } from '@reduxjs/toolkit';
import { basicApiAsyncThunkCreator } from '../../services/apiAsyncThunkCreator';
import { ThunkAPI } from '../../slices/rootReducer';
import { showSuccessMessage, showErrorMessage } from '../notifications';
import { SERVICES, HttpMethods, HttpError } from '../../services/api';
import { actions as UserListActions } from '../users/list';
import { IUser, ITeamDetails } from './user';

export interface ICreateUserDTO {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  phone?: string;
  teamId?: string;
  userType: string;
  accountId: string;
  team?: ITeamDetails;
}

export const createUser: AsyncThunk<void, ICreateUserDTO, ThunkAPI> = createAsyncThunk(
  'users/createUser',
  async (userData: ICreateUserDTO, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, ICreateUserDTO>({
        endpoint: 'users',
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(userData, thunk);
      thunk.dispatch(UserListActions.fetchUsersList({}));
      thunk.dispatch(showSuccessMessage('User created successfully'));
      return response;
    } catch(err) {
      let errorMessage = JSON.stringify(err);
      if(err instanceof HttpError) {
        errorMessage = err.message;
      }
      thunk.dispatch(showErrorMessage(errorMessage));
      return thunk.rejectWithValue(errorMessage);
    }
  }
);

export const editUser: AsyncThunk<string, ICreateUserDTO, ThunkAPI> = createAsyncThunk(
  'users/editUser',
  async (userData: ICreateUserDTO, thunk) => {
    try {
      thunk.dispatch(UserListActions.setEditingUser(userData.id || ''));
      await basicApiAsyncThunkCreator<void, ICreateUserDTO>({
        endpoint: 'users',
        method: HttpMethods.Patch,
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        contentType: 'application/json'
      })(userData, thunk);
      thunk.dispatch(UserListActions.updateUser(userData as IUser));
      thunk.dispatch(UserListActions.clearEditingUser());
      thunk.dispatch(showSuccessMessage('User updated successfully'));
      return userData.id || '';
    } catch(err) {
      let errorMessage = JSON.stringify(err);
      if(err instanceof HttpError) {
        errorMessage = err.message;
      }
      thunk.dispatch(showErrorMessage(errorMessage || 'An error occurred updating the user'));
      thunk.dispatch(UserListActions.clearEditingUser());
      return thunk.rejectWithValue(errorMessage);
    }
  }
);

export interface IResetPasswordBody {
  email: string;
}

export const resetPassword: AsyncThunk<void, IResetPasswordBody, ThunkAPI> = createAsyncThunk(
  'users/resetPassword',
  async (data: IResetPasswordBody, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, IResetPasswordBody>({
        endpoint: 'reset-password',
        microservice: SERVICES.AUTH_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);
      thunk.dispatch(showSuccessMessage('An email has been sent to this user with a link to reset their password.'));
      return response;
    } catch (err) {
      thunk.dispatch(showErrorMessage('An error occurred sending the request to reset the user password'));
      return thunk.rejectWithValue(err);
    }
  }
);
